import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { LoadingPane } from '@/components/common/States';
import { useAuthStore } from '@/stores/authStore';

/**
 * Where the API sends the browser after Microsoft signs someone in.
 *
 * The tokens arrive in the URL **fragment** rather than the query string,
 * because a fragment is never sent to a server: it stays out of access logs,
 * out of any proxy in between, and out of the `Referer` header on the next
 * click. They are read once and the address bar is rewritten immediately, so a
 * shared screenshot or a back button does not carry a live session.
 */
export default function SsoCallbackPage() {
  const navigate = useNavigate();
  const adopt = useAuthStore((state) => state.adoptSession);
  const [error, setError] = useState<string | null>(null);

  // Strict mode mounts effects twice in development; the fragment is consumed
  // and cleared on the first pass, so the second would see nothing and report a
  // failure over a sign-in that actually worked.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const accessToken = fragment.get('access_token');
    const refreshToken = fragment.get('refresh_token');
    const returnTo = fragment.get('return_to') || '/';

    // Clear before anything can go wrong, so the tokens do not survive in
    // history even if adopting the session throws.
    window.history.replaceState(null, '', window.location.pathname);

    if (!accessToken || !refreshToken) {
      setError('That sign-in link did not carry a session. Please try again.');
      return;
    }

    adopt(accessToken, refreshToken)
      .then(() => navigate(returnTo, { replace: true }))
      .catch((err: Error) => setError(err.message));
  }, [adopt, navigate]);

  if (error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 3 }}>
        <Stack spacing={1} alignItems="center">
          <Typography variant="h6">Sign-in could not be completed</Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', color: 'primary.main', mt: 1 }}
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to sign in
          </Typography>
        </Stack>
      </Box>
    );
  }

  return <LoadingPane label="Signing you in…" />;
}
